import { useEffect, useMemo, type ReactNode } from 'react';
import { useStore } from '@nanostores/react';
import { Badge, Button, Card, CodeBlock, HStack, Heading, Link, Section, Text, VStack } from '@astryxdesign/core';

import { ForecastChart } from '../components/ForecastChart';
import { PlanCards, usePlanCheckout } from '../components/PlanCards';
import { $plans, loadPlans } from '../stores/plans';
import { $auth } from '../stores/auth';

const CURL_EXAMPLE = `curl https://api.vectime.cloud/v1/forecast \\
  -H "Authorization: Bearer $VECTIME_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "series": [412, 430, 455, 471, 502, 538, 561, 590, 617, 641, 668, 702],
    "horizon": 6,
    "quantiles": true
  }'`;

const RESPONSE_EXAMPLE = `{
  "model": "timesfm-2.5-200m",
  "horizon": 6,
  "forecasts": [
    {
      "index": 0,
      "point": [728.4, 754.1, 781.9, 806.2, 833.7, 858.0],
      "quantiles": {
        "q10": [701.2, 716.8, 733.4, 748.1, 764.9, 776.3],
        "q50": [728.4, 754.1, 781.9, 806.2, 833.7, 858.0],
        "q90": [757.9, 793.0, 829.6, 866.2, 903.8, 941.1]
      }
    }
  ],
  "usage": { "series": 1, "forecast_points": 6, "requests_this_period": 42 }
}`;

/**
 * Every figure here is either a plan limit read from the API's own catalogue
 * or a row from benchmarks/results/summary.csv — the same numbers the pricing
 * page and the benchmark article show, never a rounded-up version of them.
 */
const STATS = [
  { value: '1', label: 'endpoint. POST a series, get a forecast.' },
  { value: '9', label: 'decile quantiles on every forecast, q10–q90.' },
  { value: '512', label: 'series per request on the largest plan.' },
  { value: '0', label: 'training runs. Zero-shot on TimesFM 2.5.' },
];

export function LandingPage() {
  const plans = useStore($plans);
  const auth = useStore($auth);
  const { isAuthed, onSelect, pendingCode, scheduledCode, scheduledNotice } = usePlanCheckout();

  useEffect(() => {
    void loadPlans();
  }, []);

  // A deterministic demo series so the hero chart is stable across renders.
  const demo = useMemo(makeDemoSeries, []);

  return (
    <>
      <section className="hero">
        <div className="hero__aurora" aria-hidden="true" />
        <div className="shell hero__inner">
          <div className="hero__grid">
            <VStack gap={3}>
              <HStack gap={1} vAlign="center" wrap="wrap">
                <Badge label="TimesFM 2.5 · 200M" variant="neutral" />
                <Badge label="Zero-shot · no training" variant="info" />
              </HStack>

              <Heading level={1} className="display rise">
                Forecasting as <span className="gradient-text">an HTTP call.</span>
              </Heading>

              <p className="lede measure--wide rise rise--2">
                Post a time series, get a probabilistic forecast back. VecTime Cloud runs Google&apos;s
                TimesFM foundation model behind a metered API — no model to train, no features to
                engineer, nothing to deploy.
              </p>

              <HStack gap={1.5} wrap="wrap" className="rise rise--3">
                <span className="cta-glow">
                  <Button
                    label={isAuthed ? 'Open dashboard' : 'Start free — 300 calls'}
                    variant="primary"
                    size="lg"
                    href={isAuthed ? '/app' : '/register'}
                  />
                </span>
                <Button label="Read the API docs" variant="secondary" size="lg" href="/docs" />
              </HStack>

              <div className="hero__note rise rise--3">
                <span>No card for the free tier</span>
                <span>Quantile bands included</span>
                <span>Limits in every response header</span>
              </div>
            </VStack>

            <div className="glass-card rise rise--2">
              <VStack gap={2}>
                <HStack gap={1} justify="between" vAlign="center" wrap="wrap">
                  <HStack gap={1} vAlign="center">
                    <span className="live-dot" aria-hidden="true" />
                    <Text weight="semibold">Daily orders → next 24 steps</Text>
                  </HStack>
                  <Badge label="zero-shot" variant="success" />
                </HStack>
                <ForecastChart
                  history={demo.history}
                  point={demo.point}
                  lower={demo.lower}
                  upper={demo.upper}
                  height={230}
                />
              </VStack>
            </div>
          </div>

          <div className="stat-strip rise rise--3">
            {STATS.map((s) => (
              <div key={s.label} className="stat-strip__item">
                <div className="stat-strip__value gradient-text">{s.value}</div>
                <div className="stat-strip__label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Section variant="transparent">
        <div className="shell section-block">
          <VStack gap={5}>
            <div className="section-head">
              <span className="eyebrow">Integration</span>
              <Heading level={2} className="section-title">
                Two calls to production
              </Heading>
              <Text color="secondary" size="lg">
                Create a key in the dashboard, then POST your series. That is the whole integration —
                no SDK to install, no schema to learn beyond one JSON object.
              </Text>
            </div>

            <div className="steps">
              <Step n="01" title="Create a key">
                Issue a <span className="mono">vct_live_</span> key from the dashboard. Only an HMAC
                is stored; the plaintext is shown once.
              </Step>
              <Step n="02" title="POST your series">
                Oldest first, evenly spaced. One series or up to 512 of them in a single request.
              </Step>
              <Step n="03" title="Read the band">
                The median plus nine deciles come back. Plan on the P90, alert on the P10.
              </Step>
            </div>

            <div className="split">
              <VStack gap={1.5}>
                <span className="code-label">
                  <span className="code-label__dot" />
                  Request
                </span>
                <div className="code-frame">
                  <CodeBlock code={CURL_EXAMPLE} language="bash" hasCopyButton container="card" />
                </div>
              </VStack>
              <VStack gap={1.5}>
                <span className="code-label">
                  <span className="code-label__dot code-label__dot--warm" />
                  Response
                </span>
                <div className="code-frame">
                  <CodeBlock code={RESPONSE_EXAMPLE} language="json" hasCopyButton container="card" />
                </div>
              </VStack>
            </div>
          </VStack>
        </div>
      </Section>

      <Section variant="muted">
        <div className="shell section-block">
          <VStack gap={5}>
            <div className="section-head">
              <span className="eyebrow">What you get</span>
              <Heading level={2} className="section-title">
                Everything around the model, already built
              </Heading>
              <Text color="secondary" size="lg">
                The parts you would otherwise write yourself around a research checkpoint.
              </Text>
            </div>

            <div className="grid-auto">
              <Feature
                icon={<IconBands />}
                title="Probabilistic output"
                body="Every forecast returns the median plus nine quantiles (P10–P90), so you can size safety stock or set alert thresholds instead of guessing."
              />
              <Feature
                icon={<IconStack />}
                warm
                title="Batch in one request"
                body="Send up to 512 series per call. They are forecast together in a single model pass, which is far cheaper than a request per SKU."
              />
              <Feature
                icon={<IconGauge />}
                title="Metered and rate limited"
                body="Token-bucket limits per key and an atomic monthly quota. Both are returned in RateLimit-* and X-Quota-* response headers."
              />
              <Feature
                icon={<IconKey />}
                warm
                title="Keys you can rotate"
                body="Issue and revoke keys per environment. Only an HMAC of each key is stored — the plaintext is shown exactly once."
              />
              <Feature
                icon={<IconLog />}
                title="Usage you can audit"
                body="Per-request logs with status, latency and horizon, aggregated into daily counts in the dashboard."
              />
              <Feature
                icon={<IconBolt />}
                warm
                title="Plans that take effect now"
                body="Limits are read from your plan on every request, so an upgrade applies to the very next API call."
              />
            </div>
          </VStack>
        </div>
      </Section>

      <Section variant="transparent">
        <div className="shell section-block">
          <VStack gap={5}>
            <div className="section-head">
              <span className="eyebrow">Benchmarked, not promised</span>
              <Heading level={2} className="section-title">
                300 M4 series against Prophet and seasonal-naive
              </Heading>
              <Text color="secondary" size="lg">
                We ran the comparison ourselves and published every per-series row — including the
                third of monthly series where the plain baseline beat us.
              </Text>
            </div>

            <div className="bench-grid">
              <BenchTile
                value="9.1"
                unit="sMAPE"
                label="TimesFM on M4 Hourly, against 21.2 for Prophet at defaults."
                bars={[9.095, 16.146, 21.233]}
              />
              <BenchTile
                value="82.5%"
                unit="coverage"
                label="Of held-out points inside the 80% band Hourly. Prophet's 80% band covered 65.0%."
                bars={[82.5, 65]}
              />
              <BenchTile
                value="34 / 100"
                unit="losses"
                label="Monthly series where seasonal-naive beat TimesFM. Run it on your own data first."
                bars={[34, 100]}
              />
            </div>

            <HStack gap={2} wrap="wrap" vAlign="center">
              <Button label="Read the full benchmark" variant="secondary" href="/blog/timesfm-vs-prophet-benchmark" />
              <Text size="sm" color="secondary">
                Per-series CSV and the script are{' '}
                <Link href="/benchmarks/run.py" isExternalLink>
                  downloadable
                </Link>
                . One command reruns it on a free key.
              </Text>
            </HStack>
          </VStack>
        </div>
      </Section>

      <Section variant="muted">
        <div className="shell section-block">
          <VStack gap={5}>
            <div className="section-head">
              <span className="eyebrow">Plans</span>
              <Heading level={2} className="section-title">
                Priced by volume. Same weights on every tier.
              </Heading>
              <Text color="secondary" size="lg">
                {auth.plan
                  ? `You are on ${auth.plan.name}. Priced per month by call volume; every tier runs the same model weights.`
                  : 'Calls per month, requests per minute and request size are what change. The model does not.'}
              </Text>
              {scheduledNotice && (
                <HStack gap={1} vAlign="center" wrap="wrap">
                  <Badge label="Scheduled" variant="warning" />
                  <Text size="sm" color="secondary">
                    {scheduledNotice}
                  </Text>
                </HStack>
              )}
            </div>

            {plans.length > 0 ? (
              <PlanCards
                plans={plans}
                currentCode={auth.plan?.code}
                pendingCode={pendingCode}
                scheduledCode={scheduledCode}
                onSelect={onSelect}
                ctaHref={isAuthed ? undefined : '/register'}
              />
            ) : (
              <Text color="secondary">Loading plans…</Text>
            )}

            <HStack gap={2} justify="between" wrap="wrap" vAlign="center">
              <Text color="secondary">Need a dedicated deployment or data residency?</Text>
              <Button label="Compare plans in detail" variant="secondary" href="/pricing" />
            </HStack>
          </VStack>
        </div>
      </Section>

      <div className="shell section-block--tight section-block">
        <div className="cta-band">
          <div className="cta-band__inner">
            <div>
              <div className="cta-band__title">Your first forecast is one curl away.</div>
              <div className="cta-band__text">
                300 forecasts a month on the free tier, quantile bands included, no card. Run the
                benchmark on your own series before you believe ours.
              </div>
            </div>
            <HStack gap={1.5} wrap="wrap">
              <a className="btn-light" href={isAuthed ? '/app/keys' : '/register'}>
                {isAuthed ? 'Create a key' : 'Start free'}
              </a>
              <a className="btn-ghost" href="/docs">
                API reference
              </a>
            </HStack>
          </div>
        </div>
      </div>
    </>
  );
}

function Step({ n, title, children }: { n: string; title: string; children: ReactNode }) {
  return (
    <div className="step">
      <span className="step__num">{n}</span>
      <VStack gap={0.5}>
        <Text weight="semibold">{title}</Text>
        <Text size="sm" color="secondary">
          {children}
        </Text>
      </VStack>
    </div>
  );
}

function Feature({
  icon,
  title,
  body,
  warm,
}: {
  icon: ReactNode;
  title: string;
  body: string;
  warm?: boolean;
}) {
  return (
    <Card padding={3} variant="default" className="lift">
      <div className="feature">
        <span className={`feature__icon${warm ? ' feature__icon--warm' : ''}`} aria-hidden="true">
          {icon}
        </span>
        <VStack gap={1}>
          <Text weight="semibold">{title}</Text>
          <Text size="sm" color="secondary">
            {body}
          </Text>
        </VStack>
      </div>
    </Card>
  );
}

/** A number from the benchmark, with a tiny bar sketch so the ratio is visible at a glance. */
function BenchTile({
  value,
  unit,
  label,
  bars,
}: {
  value: string;
  unit: string;
  label: string;
  bars: number[];
}) {
  const max = Math.max(...bars);
  return (
    <Card padding={3} variant="default" className="lift">
      <div className="bench-tile">
        <HStack gap={1} vAlign="end">
          <span className="bench-tile__value gradient-text">{value}</span>
          <Text size="sm" color="secondary">
            {unit}
          </Text>
        </HStack>
        <div className="bench-tile__bar" aria-hidden="true">
          {bars.map((b, i) => (
            <span key={i} style={{ height: `${Math.max(8, (b / max) * 100)}%` }} />
          ))}
        </div>
        <Text size="sm" color="secondary">
          {label}
        </Text>
      </div>
    </Card>
  );
}

/* Icons: 18px strokes on the gradient tile. Inline so there is no icon dependency. */
const ICON = { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };

function IconBands() {
  return (
    <svg {...ICON}>
      <path d="M3 17c3-6 6-6 9-3s6 3 9-3" />
      <path d="M3 12c3-6 6-6 9-3s6 3 9-3" opacity={0.5} />
      <path d="M3 21c3-6 6-6 9-3s6 3 9-3" opacity={0.5} />
    </svg>
  );
}
function IconStack() {
  return (
    <svg {...ICON}>
      <path d="m12 3 9 5-9 5-9-5 9-5Z" />
      <path d="m3 13 9 5 9-5" />
      <path d="m3 17 9 5 9-5" opacity={0.6} />
    </svg>
  );
}
function IconGauge() {
  return (
    <svg {...ICON}>
      <path d="M4 15a8 8 0 1 1 16 0" />
      <path d="m12 15 4-5" />
      <circle cx="12" cy="15" r="1.5" />
    </svg>
  );
}
function IconKey() {
  return (
    <svg {...ICON}>
      <circle cx="8" cy="14" r="4" />
      <path d="m11 11 9-9M15 7l3 3M18 4l2 2" />
    </svg>
  );
}
function IconLog() {
  return (
    <svg {...ICON}>
      <path d="M4 4h16v16H4z" />
      <path d="M8 9h8M8 13h8M8 17h5" />
    </svg>
  );
}
function IconBolt() {
  return (
    <svg {...ICON}>
      <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8Z" />
    </svg>
  );
}

/** Synthetic seasonal series + a plausible fan chart, for the hero only. */
function makeDemoSeries() {
  const history: number[] = [];
  for (let t = 0; t < 84; t++) {
    history.push(
      420 + 2.1 * t + 34 * Math.sin((2 * Math.PI * t) / 12) + 11 * Math.sin((2 * Math.PI * t) / 7),
    );
  }
  const point: number[] = [];
  const lower: number[] = [];
  const upper: number[] = [];
  for (let h = 1; h <= 24; h++) {
    const t = 83 + h;
    const v = 420 + 2.1 * t + 34 * Math.sin((2 * Math.PI * t) / 12) + 11 * Math.sin((2 * Math.PI * t) / 7);
    const spread = 14 * Math.sqrt(h);
    point.push(v);
    lower.push(v - spread);
    upper.push(v + spread);
  }
  return { history, point, lower, upper };
}
